import { useContext } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  ConfirmResult,
  createErrorResultFromException,
  createSuccessResultVoid,
  editFormDataAF,
  errors,
  highlightedAfterEditAtomFamily,
  hookFormContextAF,
  IApiError,
  IRideDto,
  IRideMetadata,
  IRideRecalculatePricePayload,
  isSoftValidationSingleErrorResponse,
  ITranslatableItem,
  Log,
  normalizeErrors,
  RecalculatePriceMode,
  RideCrudModeKey,
  RideCrudModeKeyTuple,
  RideFormFields,
  RidesApi,
  RideStatusEnum,
  RideUpdateModel,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultInvalidApiResponse,
  TError,
  TRANSLATION_KEY,
  TRANSLATION_PARAMS_KEY,
  TRydeUpdatePrice,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { DropdownSourceContext } from "../../contexts";
import { useModalConfirm } from "../../hooks-singletons";
import { useSoftValidationModalConfirm } from "../use-soft-validation-modal-confirms";
import {
  applyRideFormPriceMetadataWritesAfterRecalculate,
  areRideAdditionIdsEqual,
  composeMetadataAfterPriceRecalculate,
  pickRideAdditionsAfterPriceRecalculate,
} from "./metadata-utils";
import { useRideFormFields } from "./use-ride-form-fields";

export const useRidesRecalculatePrice = () => {
  const { t } = useTranslation();

  const [rideDtoGlobal, setRideDtoGlobal] = useRecoilState<
    Partial<IRideDto> | undefined
  >(editFormDataAF(RideCrudModeKeyTuple));

  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setLoading = useSetRecoilState(apiDataFetchingAtom);
  const setHighlightedRows = useSetRecoilState(
    highlightedAfterEditAtomFamily(RideCrudModeKey.uiUnit),
  );

  const { confirmDialogResult } = useModalConfirm();
  const { confirmSoftValidationAction } = useSoftValidationModalConfirm();
  const { mergeFormValuesToMetadata, prepareFormFieldsData } =
    useRideFormFields();
  const riceFormContext = useRecoilValue(
    hookFormContextAF(RideCrudModeKey.uiUnit),
  );
  const { getDropdownLabel } = useContext(DropdownSourceContext);

  const surfaceRecalculateWarnings = (warnings?: ITranslatableItem[]) => {
    if (!warnings?.length) {
      return;
    }
    for (const warning of warnings) {
      Log.warnAndToast(
        t(warning[TRANSLATION_KEY], warning[TRANSLATION_PARAMS_KEY]),
      );
    }
  };

  const recalculateRidePrice = useEvent(
    async (
      apiPayload: IRideRecalculatePricePayload,
    ): Promise<SimpleResult<TRydeUpdatePrice>> => {
      try {
        setErrors(undefined);
        setLoading(true);
        const recalculatePriceResult =
          await RidesApi.recalculateRidePrice(apiPayload);

        if (!recalculatePriceResult) {
          Log.errorAndToast(
            t("errors.failedToRecalculateRidesPrice"),
            "Empty api response",
          );
          return simpleResultErrorEmptyResponse(t);
        }

        if (recalculatePriceResult.success && !recalculatePriceResult.data) {
          Log.errorAndToast(
            t("errors.failedToRecalculateRidesPrice"),
            "Invalid api response",
          );
          return simpleResultInvalidApiResponse(t);
        }

        if (!recalculatePriceResult?.success) {
          if (
            !isSoftValidationSingleErrorResponse(recalculatePriceResult.data)
          ) {
            warnAndToastSimpleResult(recalculatePriceResult, t);
          }
          return recalculatePriceResult;
        }

        if (recalculatePriceResult.success && apiPayload.ride?.id) {
          setHighlightedRows([apiPayload.ride.id]);
        }

        if (!recalculatePriceResult.data?.warnings?.length) {
          Log.infoAndToast(t("ride.bulk.ridesRecalculatePrice"));
        }

        return recalculatePriceResult;
      } catch (error) {
        Log.errorAndToast(t("errors.failedToRecalculateRidesPrice"), error);
        setErrors(normalizeErrors(error as IApiError, t));
        return createErrorResultFromException(error as TError);
      } finally {
        setLoading(false);
      }
    },
  );

  const updateRidePriceDetails = async (
    rideDtoUpdated: IRideDto,
  ): Promise<SimpleResult<unknown>> => {
    if (!rideDtoUpdated) {
      return new SimpleResult({
        error: t(errors.ride.invalidRideDragIdMismatch),
      });
    }

    // Prefer explicit empty arrays from the API so a cleared rate cannot leave a stale Base price in the popover.
    const priceDetailsAfterRecalc = rideDtoUpdated.metadata?.priceDetails ?? [];
    const supplierPriceDetailsAfterRecalc =
      rideDtoUpdated.metadata?.supplierPriceDetails ?? [];
    const { additionIds, priceRateAdditions } =
      pickRideAdditionsAfterPriceRecalculate(rideDtoUpdated);
    const formValuesSnapshot = riceFormContext?.getValues() as
      Partial<IRideDto> | undefined;
    const priceLayer = {
      priceDetails: priceDetailsAfterRecalc,
      supplierPriceDetails: supplierPriceDetailsAfterRecalc,
      supplierManualPrice: rideDtoUpdated.metadata?.supplierManualPrice,
      priceRateAdditions,
    };
    const metadataAfterRecalc = composeMetadataAfterPriceRecalculate({
      rideDtoGlobalMetadata: rideDtoGlobal?.metadata,
      formValuesSnapshot,
      dirtyMetadataFields: riceFormContext?.dirtyFields?.metadata,
      priceLayer,
    });

    const newRideDto: Partial<IRideDto> = {
      ...rideDtoGlobal,
      status: rideDtoUpdated.status,
      price: rideDtoUpdated.price,
      priceRateId: rideDtoUpdated.priceRateId,
      priceRateMaxDurationInMinutes:
        rideDtoUpdated.priceRateMaxDurationInMinutes,
      priceRateMaxBoardingSegmentDurationInMinutes:
        rideDtoUpdated.priceRateMaxBoardingSegmentDurationInMinutes,
      supplierPrice: rideDtoUpdated.supplierPrice,
      supplierPriceRateId: rideDtoUpdated.supplierPriceRateId,
      additionIds,
      metadata: metadataAfterRecalc,
    };

    if (
      rideDtoGlobal?.status === RideStatusEnum.Draft &&
      rideDtoUpdated.status === RideStatusEnum.New
    ) {
      newRideDto.status = RideStatusEnum.New;
    }

    const supplierIdFormValue = riceFormContext?.getValues(
      RideFormFields.supplierId,
    );
    const vehicleTypeIdFormValue = riceFormContext?.getValues(
      RideFormFields.vehicleTypeId,
    );
    const supplierIdPriceRate = rideDtoUpdated.supplierId
      ? Number(rideDtoUpdated.supplierId)
      : undefined;
    const vehicleTypeIdPriceRate = rideDtoUpdated.vehicleTypeId
      ? Number(rideDtoUpdated.vehicleTypeId)
      : undefined;

    // Only a real change marks the form dirty; an unchanged (or unset vs empty) list must not.
    if (
      !areRideAdditionIdsEqual(
        riceFormContext?.getValues(RideFormFields.additionIds),
        additionIds,
      )
    ) {
      riceFormContext?.setValue(RideFormFields.additionIds, additionIds, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    applyRideFormPriceMetadataWritesAfterRecalculate({
      setValue: riceFormContext?.setValue,
      getValues: riceFormContext?.getValues,
      priceLayer,
    });

    if (
      Boolean(supplierIdPriceRate) &&
      supplierIdPriceRate !== Number(supplierIdFormValue)
    ) {
      newRideDto.supplierId = supplierIdPriceRate;
      riceFormContext?.setValue(RideFormFields.supplierId, supplierIdPriceRate);
      const priceRateSupplierName = await getDropdownLabel(
        "supplier",
        supplierIdPriceRate,
      );

      if (priceRateSupplierName) {
        newRideDto.supplierName = priceRateSupplierName;
      }
    }

    if (
      vehicleTypeIdPriceRate &&
      vehicleTypeIdPriceRate !== Number(vehicleTypeIdFormValue)
    ) {
      newRideDto.vehicleTypeId = vehicleTypeIdPriceRate;
      riceFormContext?.setValue(
        RideFormFields.vehicleTypeId,
        vehicleTypeIdPriceRate,
        { shouldDirty: true, shouldValidate: true },
      );

      const vehicleTypeNamePriceRate = await getDropdownLabel(
        "vehicleType",
        vehicleTypeIdPriceRate,
      );

      if (vehicleTypeNamePriceRate) {
        newRideDto.vehicleTypeName = vehicleTypeNamePriceRate;
      }
    }

    setRideDtoGlobal(newRideDto);

    return createSuccessResultVoid();
  };

  const prepareRecalculatePayload = useEvent(
    (): IRideRecalculatePricePayload => {
      const isEdit = !!rideDtoGlobal?.id;
      const formValuesSnapshot = riceFormContext?.getValues() as
        Partial<IRideDto> | undefined;

      Log.debug(
        `Ride form values snapshot for price recalculate:`,
        formValuesSnapshot,
      );
      const metadataComputed: IRideMetadata = {
        ...(rideDtoGlobal?.metadata || {}),
      };

      mergeFormValuesToMetadata(formValuesSnapshot, metadataComputed);
      Log.debug(
        "Ride form metadata composed with form values:",
        metadataComputed,
      );

      const formDataCleaned = prepareFormFieldsData(
        formValuesSnapshot,
        false,
        isEdit,
      );

      const rideForApiSend: RideUpdateModel = {
        ...rideDtoGlobal,
        ...formDataCleaned,
        metadata: metadataComputed,
        dryRun: true,
        isReturnDtoForGrid: false,
      };

      const supplierId = riceFormContext?.getValues(RideFormFields.supplierId);
      const vehicleTypeId = riceFormContext?.getValues(
        RideFormFields.vehicleTypeId,
      );

      const isCurrentPriceRateLocked = Boolean(
        rideForApiSend.id && rideForApiSend.priceRateId,
      );

      return {
        ride: rideForApiSend,
        lockSupplier: Boolean(supplierId),
        lockVehicleType: Boolean(vehicleTypeId),
        lockCurrentPriceRateIfExists: isCurrentPriceRateLocked,
        mode: RecalculatePriceMode.Both,
      };
    },
  );

  const handleRecalculatePriceClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.RECALCULATE_PRICE_MESSAGE`),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const apiPayload = prepareRecalculatePayload();
    let recalculatePriceResponse = await recalculateRidePrice(apiPayload);
    Log.debug("Recalculate price response data", recalculatePriceResponse.data);

    if (
      !recalculatePriceResponse?.success &&
      isSoftValidationSingleErrorResponse(recalculatePriceResponse.data)
    ) {
      const softValidationResult = await confirmSoftValidationAction(
        recalculatePriceResponse.data,
      );
      if (softValidationResult !== ConfirmResult.OK) {
        return;
      }
      recalculatePriceResponse = await recalculateRidePrice({
        ...apiPayload,
        token: recalculatePriceResponse.data.token,
      });
    }

    if (recalculatePriceResponse?.success && recalculatePriceResponse.data) {
      const updatedRideDto = recalculatePriceResponse.data;
      surfaceRecalculateWarnings(updatedRideDto.warnings);
      updateRidePriceDetails(updatedRideDto);
    }
  });

  const handleRecalculateSupplierPriceClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.RECALCULATE_PRICE_MESSAGE`),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const apiPayload = prepareRecalculatePayload();
    let recalculatePriceResponse =
      await RidesApi.recalculateSupplierPriceRate(apiPayload);

    if (!recalculatePriceResponse) {
      Log.errorAndToast(
        t("errors.failedToRecalculateRidesPrice"),
        "Empty api response",
      );
      return;
    }

    if (recalculatePriceResponse.success && !recalculatePriceResponse.data) {
      Log.errorAndToast(
        t("errors.failedToRecalculateRidesPrice"),
        "Invalid api response",
      );
      return;
    }

    if (
      !recalculatePriceResponse?.success &&
      isSoftValidationSingleErrorResponse(recalculatePriceResponse.data)
    ) {
      const softValidationResult = await confirmSoftValidationAction(
        recalculatePriceResponse.data,
      );
      if (softValidationResult !== ConfirmResult.OK) {
        return;
      }
      recalculatePriceResponse = await RidesApi.recalculateSupplierPriceRate({
        ...apiPayload,
        token: recalculatePriceResponse.data.token,
      });
    }

    if (!recalculatePriceResponse) {
      return;
    }
    if (!recalculatePriceResponse.success) {
      if (!isSoftValidationSingleErrorResponse(recalculatePriceResponse.data)) {
        warnAndToastSimpleResult(recalculatePriceResponse, t);
      }
      return;
    }

    if (apiPayload.ride?.id) {
      setHighlightedRows([apiPayload.ride.id]);
    }

    if (!recalculatePriceResponse.data?.warnings?.length) {
      Log.infoAndToast(t("ride.bulk.ridesRecalculatePrice"));
    }
    Log.debug(
      "Recalculate supplier price response data",
      recalculatePriceResponse.data,
    );

    if (recalculatePriceResponse.data) {
      const updatedRideDto = recalculatePriceResponse.data;
      surfaceRecalculateWarnings(updatedRideDto.warnings);
      updateRidePriceDetails(updatedRideDto);
    }
  });

  return {
    updateRidePriceDetails,
    recalculateRidePrice,
    handleRecalculatePriceClick,
    handleRecalculateSupplierPriceClick,
  };
};
